import type { LocaleMorpheus } from "@solvari/translations";

import {
  getEnv,
  getSentry,
  isClient,
  memoize,
  toSearchParams,
  useGlobal,
} from "@solvari/utils";

type Ziggy = {
  routes: Record<string, { domain?: string; methods: string[]; uri: string }>;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  var Ziggy: Ziggy | undefined;
}

function getZiggy(): Ziggy {
  const ziggy = isClient()
    ? (Ziggy ?? useGlobal<Ziggy>("Ziggy").get())
    : useGlobal<Ziggy>("Ziggy").get();
  if (!ziggy?.routes) {
    throw new Error("Tried to get ziggy routes but they were not defined");
  }
  return ziggy;
}

const routesByName = memoize(
  (_locale: LocaleMorpheus): Record<string, string> => {
    const ziggy = getZiggy();
    function parseDomain(domain: string | undefined): string {
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (!domain || domain === window?.location.hostname) {
        return "";
      }
      return domain.includes("http") ? domain : `https://${domain}`;
    }
    return Object.fromEntries(
      Object.entries(ziggy.routes).map(([name, route]): [string, string] => {
        const path = route.uri === "/" ? "" : route.uri;
        return [name, `${parseDomain(route.domain)}/${path}`];
      }),
    );
  },
);

const ziggyGet = memoize(
  (
    locale: LocaleMorpheus,
    routeName: string,
    params?: Record<string, number | string | undefined>,
  ): string => {
    const route = routesByName(locale)[routeName];

    if (!route) {
      getSentry().captureException(
        new Error(
          `Tried to get ziggy route '${routeName}' but it was not defined`,
        ),
      );
      return routeName;
    }

    if (!params) {
      return route;
    }

    return Object.entries(params).reduce((route, [param, value]) => {
      return route.replace(
        new RegExp(`/\\{${param}\\??}`),
        value !== undefined ? `/${String(value)}` : "",
      );
    }, route);
  },
);

function createZiggyFn(locale: LocaleMorpheus): typeof ziggyUnsafe {
  return (routeName, pathParams?, searchParams?) => {
    const path = ziggyGet(locale, routeName, pathParams);
    if (!searchParams) {
      return path;
    }
    return `${path}?${toSearchParams(searchParams)}`;
  };
}

/**
 * Generates a full URL from a Laravel route name and parameters.
 * Resolves the locale from the global env on each call.
 * Each app should create a typed wrapper.
 *
 * @example
 * const ziggy = ziggyUnsafe as ZiggyClient;
 * ziggy("api.v2.my-solvari.application", { guid: "abc-123" })
 */
function ziggyUnsafe(
  routeName: string,
  pathParams?: Record<string, number | string | undefined>,
  searchParams?: Record<string, unknown>,
): string {
  return createZiggyFn(getEnv().config.localeCode)(
    routeName,
    pathParams,
    searchParams,
  );
}

function ziggyPathToRouterPath(
  path: string,
  paramConstraints?: Record<string, string>,
) {
  return path.replace(
    /\/\{(\w+)(\??)}/g,
    (_substring, param: string, optional) => {
      const constraint = paramConstraints?.[param];
      const regex = constraint ? `(${constraint})` : "";
      return `/:${param}${regex}${optional}`;
    },
  );
}

/**
 * Converts a Laravel route name to a Vue Router compatible path.
 * Transforms Laravel route parameters `{param}` to Vue Router format `:param`.
 * This is the untyped version - each app should create a typed wrapper.
 *
 * @param routeName - The Laravel route name (e.g., "client.my_solvari.applications.details")
 * @param paramConstraints
 * @returns The path in Vue Router format (e.g., "/my-solvari/applications/details/:guid")
 *
 * @example
 * // In your app's shared/libs/ziggy.ts:
 * import type { ZiggyClientRoutes } from "@solvari/morpheus-api";
 * import { ziggyUnsafeRouter } from "@solvari/common-fe/integrations";
 * const ziggyRouter = ziggyUnsafeRouter as ZiggyClientRoutes;
 *
 * // Usage in Vue Router config:
 * { path: ziggyRouter("client.my_solvari.applications.details"), component: ... }
 * // => { path: "/my-solvari/applications/details/:guid", component: ... }
 */
function ziggyUnsafeRouter(
  routeName: string,
  paramConstraints?: Record<string, string>,
) {
  return ziggyPathToRouterPath(
    ziggyGet(getEnv().config.localeCode, routeName),
    paramConstraints,
  );
}

/**
 * Composable that captures the locale and Ziggy routes at call time,
 * returning a ziggy function that works outside the Nuxt async context
 * (e.g. useHead/useSeoMeta getters, deferred computeds).
 *
 * Call during component setup, use the returned function in computeds/getters.
 *
 * @example
 * const ziggy = useZiggyUnsafe();
 * useSeoNormalized({ canonical: () => ziggy("mainSite.companyWall") });
 */
function useZiggyUnsafe(): typeof ziggyUnsafe {
  const locale = getEnv().config.localeCode;
  routesByName(locale);

  return createZiggyFn(locale);
}

export {
  useZiggyUnsafe,
  ziggyPathToRouterPath,
  ziggyUnsafe,
  ziggyUnsafeRouter,
};
export type { Ziggy };
